import {
	ContextMenu,
	ContextMenuDeleteWithModelUsageItem,
	ContextMenuItem,
	IDrawerDataListColumn,
	PaginatedDrawerAutoDataList,
	laravelPaginatedListFetchConfig,
	useHasPermission,
	useLaravelCrudEntity,
} from "@dgs/core";
import { useQueryClient } from "@tanstack/react-query";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { PermissionName } from "../../../shared/types/permission";
import { contactService } from "../shared/api/contactService";
import { contactViewService } from "../shared/api/contactViewService";
import { dataFieldService } from "../shared/api/dataFieldService";
import { userPreferencesService } from "../shared/api/userPreferencesService";
import { userService } from "../shared/api/userService";
import { ContactViewFormValues, ContactViewIndexResource, ContactViewRequest } from "../shared/types/contactView";
import { ContactViewCreateDrawer, ContactViewUpdateDrawer } from "./ContactViewDrawer";

const mapToRequest = ({ contactAttributes, ...contactView }: ContactViewFormValues): ContactViewRequest => ({
	...contactView,
	contactAttributeIds: contactAttributes.map((attribute) => attribute.id),
});

export const ContactViewList = () => {
	const { t } = useTranslation();
	const queryClient = useQueryClient();
	const crudOperations = useLaravelCrudEntity(
		contactViewService,
		{
			entityName: t("Contact view"),
			messages: {
				create: t("Contact view was saved."),
				update: t("Contact view was saved."),
				delete: t("Contact view was deleted."),
			},
		},
		[
			userPreferencesService.showKey,
			contactService.listKey,
			contactViewService.indexAllKey,
			dataFieldService.indexByContactViewKey,
		],
		[userService.indexKey],
	);
	const canCreate = useHasPermission<PermissionName>(["contactView.create"]);
	const canUpdate = useHasPermission<PermissionName>(["contactView.update"]);

	const dataListColumns: IDrawerDataListColumn<ContactViewIndexResource>[] = useMemo(
		() => [
			{
				heading: t("Name"),
				valueKey: "name",
				type: "value",
			},
			{
				heading: "",
				type: "actions",
				size: "max-content",
				action: ({ id }, openDrawer) => (
					<ContextMenu displayShortcutsAs="icon">
						<ContextMenuItem
							title={t("Edit contact view")}
							label={t("Edit contact view")}
							action={openDrawer}
							icon="edit"
							shortcut="primary"
						/>
						<ContextMenuDeleteWithModelUsageItem
							deleteAction={async () => {
								await crudOperations.deleteEntity(id);
								await queryClient.invalidateQueries({ queryKey: contactViewService.indexAllKey });
							}}
							showUsageAction={() => contactViewService.usages(id).then((r) => r.data.data)}
							label={t("Delete contact view")}
							title={t("Delete contact view")}
							modelName={t("Contact view")}
							confirmationBody={t("Are you sure you want to delete this contact view?")}
						/>
					</ContextMenu>
				),
			},
		],
		[crudOperations, queryClient, t],
	);

	return (
		<PaginatedDrawerAutoDataList
			canCreate={canCreate}
			canUpdate={canUpdate}
			columns={dataListColumns}
			listFetchConfig={laravelPaginatedListFetchConfig(contactViewService.listKey, contactViewService.index)}
			renderCreateDrawer={(drawerProps) => (
				<ContactViewCreateDrawer
					{...drawerProps}
					onSubmit={async (entityFormState) => crudOperations.createEntity(mapToRequest(entityFormState))}
				/>
			)}
			renderUpdateDrawer={(drawerProps) => (
				<ContactViewUpdateDrawer
					{...drawerProps}
					onSubmit={(entityFormState) => crudOperations.updateEntity(drawerProps.id, mapToRequest(entityFormState))}
				/>
			)}
			translations={{
				title: t("Contact views"),
				empty: t("No contact view available."),
			}}
			initialSearch={{ search: "", searchFields: [{ name: "name", label: t("Name") }] }}
		/>
	);
};
